/* =====================================================================
   cloudIcons.ts — deterministic, catalog-driven vendor icon resolution.

   Enterprise-grade approach (no reliance on LLM free-text for the asset):
   - vendor is inferred from the Terraform resource-type prefix
     (aws_ / azurerm_ / azuread_ / google_),
   - the resource type maps to a vendor-neutral CANONICAL KEY,
   - the CATALOG records which (vendor, key) pairs have a real asset on the
     image CDN, so we only ever emit an <img> when the file exists; everything
     else falls back to the monoline glyph.

   Assets live in the S3 image bucket under `visualization/cloud/<vendor>/<key>.svg`
   and are served via CloudFront (see scripts/sync-cloud-icons.sh). The on-disk
   source of truth is assets/cloud-icons/.
   ===================================================================== */

import { getImageUrl } from "@/lib/imagePaths";
import type { Node as ArchNode } from "./types";

export type Vendor = "aws" | "azure" | "gcp";

/* Which (vendor → canonical key) assets actually exist on the CDN. Keep in sync
   with assets/cloud-icons/ (and the sync script). Unknown combos fall back to a
   glyph, so adding an asset is: drop the file + add its key here. */
const CATALOG: Record<Vendor, ReadonlySet<string>> = {
  aws: new Set([
    "compute.vm", "database.sql", "storage.object",
    "network.vpc", "network.nat-gateway", "network.internet-gateway",
    "network.vpc-endpoint", "network.load-balancer",
    "security.kms", "security.iam", "security.threat-detection", "security.posture",
    "security.compliance", "security.audit-log", "security.secrets",
    "mgmt.monitoring", "mgmt.ssm", "finops.budget",
  ]),
  azure: new Set([
    "compute.vm", "database.sql", "storage.object",
    "network.vpc", "network.load-balancer", "network.nat-gateway",
    "security.kms", "security.secrets", "security.iam", "security.posture",
    "security.audit-log", "mgmt.monitoring", "finops.budget",
  ]),
  gcp: new Set([
    "compute.vm", "database.sql", "storage.object",
    "security.posture", "security.threat-detection",
  ]),
};

/* Exact Terraform resource type → canonical key. Add entries here as new
   resources appear; an unmapped type simply falls back to the glyph. */
const RESOURCE_TYPE_TO_KEY: Record<string, string> = {
  // ---- AWS ----
  aws_instance: "compute.vm",
  aws_db_instance: "database.sql",
  aws_rds_cluster: "database.sql",
  aws_rds_cluster_instance: "database.sql",
  aws_s3_bucket: "storage.object",
  aws_vpc: "network.vpc",
  aws_nat_gateway: "network.nat-gateway",
  aws_internet_gateway: "network.internet-gateway",
  aws_vpc_endpoint: "network.vpc-endpoint",
  aws_lb: "network.load-balancer",
  aws_alb: "network.load-balancer",
  aws_elb: "network.load-balancer",
  aws_kms_key: "security.kms",
  aws_kms_alias: "security.kms",
  aws_iam_role: "security.iam",
  aws_iam_user: "security.iam",
  aws_iam_policy: "security.iam",
  aws_iam_instance_profile: "security.iam",
  aws_guardduty_detector: "security.threat-detection",
  aws_securityhub_account: "security.posture",
  aws_config_configuration_recorder: "security.compliance",
  aws_config_config_rule: "security.compliance",
  aws_cloudtrail: "security.audit-log",
  aws_secretsmanager_secret: "security.secrets",
  aws_cloudwatch_log_group: "mgmt.monitoring",
  aws_cloudwatch_metric_alarm: "mgmt.monitoring",
  aws_cloudwatch_dashboard: "mgmt.monitoring",
  aws_ssm_parameter: "mgmt.ssm",
  aws_ssm_document: "mgmt.ssm",
  aws_ssm_association: "mgmt.ssm",
  aws_budgets_budget: "finops.budget",

  // ---- Azure ----
  azurerm_linux_virtual_machine: "compute.vm",
  azurerm_windows_virtual_machine: "compute.vm",
  azurerm_virtual_machine: "compute.vm",
  azurerm_mssql_database: "database.sql",
  azurerm_mssql_server: "database.sql",
  azurerm_sql_database: "database.sql",
  azurerm_postgresql_flexible_server: "database.sql",
  azurerm_mysql_flexible_server: "database.sql",
  azurerm_storage_account: "storage.object",
  azurerm_virtual_network: "network.vpc",
  azurerm_lb: "network.load-balancer",
  azurerm_nat_gateway: "network.nat-gateway",
  azurerm_key_vault: "security.kms",
  azurerm_key_vault_secret: "security.secrets",
  azurerm_user_assigned_identity: "security.iam",
  azurerm_role_assignment: "security.iam",
  azurerm_security_center_subscription_pricing: "security.posture",
  azurerm_monitor_diagnostic_setting: "mgmt.monitoring",
  azurerm_monitor_metric_alert: "mgmt.monitoring",
  azurerm_consumption_budget_subscription: "finops.budget",
  azurerm_consumption_budget_resource_group: "finops.budget",

  // ---- GCP ----
  google_compute_instance: "compute.vm",
  google_sql_database_instance: "database.sql",
  google_sql_database: "database.sql",
  google_storage_bucket: "storage.object",
  google_scc_source: "security.posture",
  google_security_center_source: "security.posture",
};

const VENDOR_PREFIX: Array<[RegExp, Vendor]> = [
  [/^aws_/, "aws"],
  [/^(azurerm_|azuread_|azapi_)/, "azure"],
  [/^google_/, "gcp"],
];

function vendorOf(resourceType: string): Vendor | null {
  for (const [re, v] of VENDOR_PREFIX) if (re.test(resourceType)) return v;
  return null;
}

/* Secondary, best-effort match on the node's service/label text, for diagrams
   whose nodes don't (yet) carry a resourceType — the derive step may emit a
   friendly label like "EC2"/"Oracle RDS". Keywords are vendor-distinctive
   (AWS/Azure/GCP product names); ambiguous generic terms are intentionally
   omitted so they fall through to the glyph rather than guess the vendor.
   resourceType remains the primary, authoritative key. */
const KEYWORD_RULES: Array<{ re: RegExp; vendor: Vendor; key: string }> = [
  // AWS
  { re: /guardduty/i, vendor: "aws", key: "security.threat-detection" },
  { re: /security hub/i, vendor: "aws", key: "security.posture" },
  { re: /cloudtrail/i, vendor: "aws", key: "security.audit-log" },
  { re: /cloudwatch/i, vendor: "aws", key: "mgmt.monitoring" },
  { re: /secrets manager/i, vendor: "aws", key: "security.secrets" },
  { re: /\bkms\b|key management/i, vendor: "aws", key: "security.kms" },
  { re: /\biam\b|identity and access/i, vendor: "aws", key: "security.iam" },
  { re: /aws config|config recorder/i, vendor: "aws", key: "security.compliance" },
  { re: /systems manager|\bssm\b/i, vendor: "aws", key: "mgmt.ssm" },
  { re: /budget/i, vendor: "aws", key: "finops.budget" },
  { re: /nat gateway/i, vendor: "aws", key: "network.nat-gateway" },
  { re: /internet gateway/i, vendor: "aws", key: "network.internet-gateway" },
  { re: /vpc endpoint|interface endpoint|gateway endpoint/i, vendor: "aws", key: "network.vpc-endpoint" },
  { re: /\bec2\b/i, vendor: "aws", key: "compute.vm" },
  { re: /\brds\b|aurora/i, vendor: "aws", key: "database.sql" },
  { re: /\bs3\b|simple storage/i, vendor: "aws", key: "storage.object" },
  { re: /\bvpc\b|virtual private cloud/i, vendor: "aws", key: "network.vpc" },
  // Azure
  { re: /key vault/i, vendor: "azure", key: "security.kms" },
  { re: /defender/i, vendor: "azure", key: "security.posture" },
  { re: /blob|storage account/i, vendor: "azure", key: "storage.object" },
  { re: /virtual network|vnet/i, vendor: "azure", key: "network.vpc" },
  // GCP
  { re: /compute engine/i, vendor: "gcp", key: "compute.vm" },
  { re: /cloud sql/i, vendor: "gcp", key: "database.sql" },
  { re: /cloud storage/i, vendor: "gcp", key: "storage.object" },
  { re: /security command center/i, vendor: "gcp", key: "security.posture" },
];

function keywordMatch(node: ArchNode): { vendor: Vendor; key: string } | null {
  const hay = `${node.service || ""} ${node.label || ""}`;
  for (const r of KEYWORD_RULES) if (r.re.test(hay)) return { vendor: r.vendor, key: r.key };
  return null;
}

/** Pull a Terraform resource type from the node — the dedicated field if the
    derive step provided it, otherwise sniff it out of the `service` string. */
function resourceTypeOf(node: ArchNode): string | null {
  if (node.resourceType) return node.resourceType.trim();
  const m = (node.service || "").match(/\b((?:aws|azurerm|azuread|google)_[a-z0-9_]+)\b/i);
  return m ? m[1].toLowerCase() : null;
}

/**
 * Resolve a node to a vendor-logo URL, or null to fall back to the glyph.
 * Deterministic: resourceType → vendor + canonical key → catalog → CDN URL.
 */
export function resolveCloudIcon(node: ArchNode): string | null {
  // 1) Primary: deterministic resource-type → vendor + canonical key.
  const rt = resourceTypeOf(node);
  if (rt) {
    const vendor = vendorOf(rt);
    const key = RESOURCE_TYPE_TO_KEY[rt];
    if (vendor && key && CATALOG[vendor].has(key)) return getImageUrl(`cloud/${vendor}/${key}.svg`);
  }
  // 2) Fallback: vendor-distinctive keyword in the service/label text.
  const kw = keywordMatch(node);
  if (kw && CATALOG[kw.vendor].has(kw.key)) return getImageUrl(`cloud/${kw.vendor}/${kw.key}.svg`);
  return null;
}
